import {zodResolver} from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {useConfirm} from '@/components/confirm-dialog/useConfirm';
import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form} from '@/components/ui/form';
import {FormSkeleton} from '@/components/ui/skeleton';
import {useToast} from '@/components/ui/use-toast';
import {useAuthorize} from '@/hooks/useAuth';
import {graphqlClient} from '@/services/graphqlClient';
import {
  type HealthLogMeasurement,
  useDeleteHealthLogMeasurementMutation,
  useGetHealthLogMeasurementsQuery,
  useUpdateHealthLogMeasurementMutation,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';

import type {HealthLogMeasurementFormValues} from '../utils/healthFormInput';
import {
  healthLogMeasurementFormDefaultValues,
  healthLogMeasurementFormSchema,
  mapHealthLogMeasurementFormValuesToMeasurementInput,
} from '../utils/healthFormInput';
import HealthLogMeasurementFormFields from './HealthLogMeasurementFormFields';

export type EditHealthLogMeasurementDialogProps = DialogProps;

const EditHealthLogMeasurementDialog = ({
  onOpenChange,
  ...props
}: EditHealthLogMeasurementDialogProps) => {
  const {t} = useTranslation();
  const {toast} = useToast();
  const {confirm} = useConfirm();
  const [searchParams] = useSearchParams();

  const canUpdate = useAuthorize('UPDATE_HEALTH_LOG_MEASUREMENT');
  const canDelete = useAuthorize('DELETE_HEALTH_LOG_MEASUREMENT');

  const residentId = searchParams.get('residentId') || '';
  const metricId = searchParams.get('metricId') || '';
  const recordedAt = dayjs(
    searchParams.get('editHealthLog') || undefined
  ).toISOString();

  const {data: measurementsData, isLoading} = useGetHealthLogMeasurementsQuery(
    graphqlClient,
    {
      residentId,
      metricId,
      from: recordedAt,
      to: recordedAt,
      limit: 1,
    },
    {
      enabled: Boolean(residentId && metricId && recordedAt),
    }
  );

  const measurement: HealthLogMeasurement | undefined =
    measurementsData?.getHealthLogMeasurements?.items?.[0];

  const onSuccess = () => {
    queryClient.invalidateQueries({queryKey: ['GetHealthLogMeasurements']});
  };

  const updateMeasurement = useUpdateHealthLogMeasurementMutation(
    graphqlClient,
    {onSuccess}
  );

  const deleteMeasurement = useDeleteHealthLogMeasurementMutation(
    graphqlClient,
    {onSuccess}
  );

  const form = useForm<HealthLogMeasurementFormValues>({
    resolver: zodResolver(healthLogMeasurementFormSchema),
    defaultValues: measurement
      ? healthLogMeasurementFormDefaultValues(measurement)
      : {
          residentId,
          metricId,
          recordedAt,
        },
  });

  useEffect(() => {
    if (!measurement) return;
    form.reset(healthLogMeasurementFormDefaultValues(measurement), {
      keepDirty: false,
      keepTouched: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measurement]);

  const handleSubmit = async (values: HealthLogMeasurementFormValues) => {
    if (!measurement) return;

    try {
      const mapped =
        mapHealthLogMeasurementFormValuesToMeasurementInput(values);

      await updateMeasurement.mutateAsync({
        input: {
          residentId: measurement.residentId,
          metricId: measurement.metricId,
          recordedAt: measurement.recordedAt,
          values: mapped.values,
        },
      });

      toast({title: t('Measurement updated'), variant: 'success'});
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async () => {
    if (!measurement) return;

    try {
      await confirm({
        title: t('Delete measurement'),
        message: t(
          'Are you sure you want to delete this health log measurement?'
        ),
      });

      await deleteMeasurement.mutateAsync({
        residentId: measurement.residentId,
        metricId: measurement.metricId,
        recordedAt: measurement.recordedAt,
      });

      toast({title: t('Measurement deleted'), variant: 'success'});
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const hasMeasurement = Boolean(measurement);
  const isPending =
    updateMeasurement.isPending ||
    deleteMeasurement.isPending ||
    !hasMeasurement;

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={onOpenChange}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader>
              <DialogTitle>{t('Health log entry')}</DialogTitle>
            </DialogHeader>
            <DialogSection>
              {isLoading && <FormSkeleton />}
              {!isLoading && !measurement && (
                <div>{t('Measurement not found')}</div>
              )}
              {measurement && (
                <HealthLogMeasurementFormFields form={form} isEdit />
              )}
            </DialogSection>
            <DialogFooter>
              {hasMeasurement ? (
                <>
                  <Button
                    type="button"
                    disabled={isPending || !canDelete}
                    className="sm:mr-auto"
                    variant="warning"
                    onClick={handleDelete}
                  >
                    {t('Delete measurement')}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => onOpenChange?.(false)}
                  >
                    {t('Close')}
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isPending || !canUpdate}
                  >
                    {t('Submit')}
                  </Button>
                </>
              ) : (
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => onOpenChange?.(false)}
                >
                  {t('Close')}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default EditHealthLogMeasurementDialog;
